/**
 * Desk Setup Builder - bundle tier selector.
 *
 * Self-contained on purpose: no @theme imports, so an unrelated module failing
 * to load can never stop this from upgrading. The section's reveal animation is
 * driven by its own inline IntersectionObserver and does not depend on this file
 * at all - this only handles selection, totals and the cart POST.
 */

const SELECTED = 'aria-checked';

class DeskSetupBuilder extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;

    this.tiers = Array.from(this.querySelectorAll('.dsb__tier'));
    this.cta = this.querySelector('[data-role="cta"]');
    this.countEl = this.querySelector('[data-role="count"]');
    this.totalEl = this.querySelector('[data-role="total"]');
    this.errorEl = this.querySelector('[data-role="error"]');

    this.currency = this.dataset.currency || 'USD';
    this.totalLabel = this.dataset.totalLabel || 'Setup total';
    this.ctaLabel = this.dataset.ctaLabel || 'Add setup to cart';
    this.ctaEmptyLabel = this.dataset.ctaEmptyLabel || 'Pick at least one item';

    this.tiers.forEach((tier) => {
      const cards = Array.from(tier.querySelectorAll('.dsb__card:not(.dsb__card--placeholder)'));
      cards.forEach((card) => {
        card.addEventListener('click', () => this.toggle(tier, cards, card));
        card.addEventListener('keydown', (e) => this.onKeydown(e, cards, card));
      });
    });

    if (this.cta) this.cta.addEventListener('click', () => this.addToCart());

    this.render();
  }

  toggle(tier, cards, card) {
    if (card.hasAttribute('disabled')) return;
    const wasSelected = card.getAttribute(SELECTED) === 'true';
    cards.forEach((c) => c.setAttribute(SELECTED, 'false'));
    // Tiers are optional, so clicking the chosen card again clears the step.
    card.setAttribute(SELECTED, wasSelected ? 'false' : 'true');
    tier.classList.toggle('is-chosen', !wasSelected);
    this.render();
  }

  onKeydown(e, cards, card) {
    const i = cards.indexOf(card);
    let next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = cards[(i + 1) % cards.length];
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = cards[(i - 1 + cards.length) % cards.length];
    if (!next) return;
    e.preventDefault();
    next.focus();
  }

  get selection() {
    return this.tiers
      .map((t) => t.querySelector('.dsb__card[aria-checked="true"]'))
      .filter(Boolean);
  }

  money(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.currency,
      }).format(cents / 100);
    } catch (_) {
      return (cents / 100).toFixed(2);
    }
  }

  render() {
    const chosen = this.selection;
    const total = chosen.reduce((sum, c) => sum + (parseInt(c.dataset.price, 10) || 0), 0);

    if (this.countEl) {
      this.countEl.textContent =
        chosen.length === 0
          ? 'No items selected'
          : `${chosen.length} item${chosen.length === 1 ? '' : 's'} selected`;
    }
    if (this.totalEl) {
      this.totalEl.textContent = chosen.length ? `${this.totalLabel}: ${this.money(total)}` : '';
    }
    if (this.cta) {
      this.cta.disabled = chosen.length === 0;
      this.cta.textContent = chosen.length === 0 ? this.ctaEmptyLabel : this.ctaLabel;
    }
    this.setError('');
  }

  setError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.hidden = !msg;
  }

  async addToCart() {
    const chosen = this.selection;
    if (!chosen.length || !this.cta) return;

    const items = chosen.map((c) => ({ id: Number(c.dataset.variantId), quantity: 1 }));

    let url = this.dataset.cartAddUrl || '/cart/add';
    if (!url.endsWith('.js')) url += '.js';

    this.cta.classList.add('is-busy');
    this.cta.disabled = true;
    this.setError('');

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        let message = 'Something went wrong adding these to your cart.';
        try {
          const body = await res.json();
          if (body && body.description) message = body.description;
        } catch (_) {
          /* keep the generic message */
        }
        throw new Error(message);
      }

      if ((this.dataset.afterAdd || 'cart') === 'cart') {
        window.location.href = this.dataset.cartUrl || '/cart';
        return;
      }

      // Staying put: let the theme's cart UI know something changed. Harmless
      // if nothing is listening.
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      this.setError(`Added ${items.length} item${items.length === 1 ? '' : 's'} to your cart.`);
    } catch (err) {
      this.setError(err.message);
    } finally {
      this.cta.classList.remove('is-busy');
      this.render();
    }
  }
}

if (!customElements.get('desk-setup-builder-component')) {
  customElements.define('desk-setup-builder-component', DeskSetupBuilder);
}
